import React, { useState, useEffect, useCallback } from "react";
import { Table } from "react-bootstrap";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const ServiceStatusDashboard = () => {
  const [serviceStatus, setServiceStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadStatus = useCallback(async (runChecks = false) => {
    if (runChecks) setRefreshing(true);
    else setLoading(true);
    setFeedback(null);
    try {
      const data = runChecks ? await api.refreshServiceStatus() : await api.getServiceStatus();
      setServiceStatus(data);
      setLastRefresh(new Date().toLocaleTimeString("en-IN"));
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadStatus(true);
    const interval = setInterval(() => loadStatus(true), 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  const offlineCount = serviceStatus.filter((s) => s.statusKey === "offline").length;
  const degradedCount = serviceStatus.filter((s) => s.statusKey === "degraded").length;

  if (loading && !serviceStatus.length) return <LoadingState message="Running health checks..." />;

  return (
    <div>
      <PageHeader
        title="Service Status"
        subtitle="Live API health for bill payment providers"
        action={
          <button className="btn-billr btn-billr-primary" disabled={refreshing} onClick={() => loadStatus(true)}>
            {refreshing ? "Checking..." : "↻ Refresh Now"}
          </button>
        }
      />

      {feedback && <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />}
      {offlineCount > 0 && <AlertBanner type="danger" message={`${offlineCount} service(s) offline — immediate attention required`} />}
      {degradedCount > 0 && <AlertBanner type="warning" message={`${degradedCount} service(s) running with degraded performance`} />}
      {lastRefresh && <p className="text-muted small mb-3">Last checked {lastRefresh} · Auto-refresh every 30s</p>}

      <DataCard title="External APIs">
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Service</th>
              <th>Status</th>
              <th>Latency</th>
              <th>Last Checked</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {serviceStatus.map((service) => (
              <tr key={service.serviceId} className={service.statusKey === "offline" ? "row-flagged" : service.statusKey === "degraded" ? "row-warning" : ""}>
                <td><strong>{service.name}</strong></td>
                <td><StatusBadge status={service.statusKey} label={service.status} /></td>
                <td>{service.latency != null ? `${service.latency} ms` : "—"}</td>
                <td>{service.lastChecked}</td>
                <td className="small text-muted">{service.errorMessage || "OK"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>
    </div>
  );
};

export default ServiceStatusDashboard;
